import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const MarineEngineering: React.FC = () => {
  const vesselTypes = [
    {
      title: "Fishing Boats",
      description: "Custom-designed fishing vessels optimized for local fishing conditions and requirements.",
      icon: "🎣",
      features: ["Efficient hull design", "Fish storage systems", "Navigation equipment"]
    },
    {
      title: "Passenger Ferries",
      description: "Safe and comfortable passenger vessels for community transportation.",
      icon: "⛴️",
      features: ["Safety systems", "Comfortable seating", "Weather protection"]
    },
    {
      title: "Cargo Vessels",
      description: "Commercial cargo ships designed for efficient transport of goods.",
      icon: "🚢",
      features: ["Cargo handling systems", "Loading equipment", "Storage optimization"]
    },
    {
      title: "Luxury Yachts",
      description: "Premium yacht construction with modern amenities and advanced technology.",
      icon: "🛥️",
      features: ["Luxury interiors", "Advanced navigation", "Entertainment systems"]
    }
  ];

  const services = [
    "Custom vessel design and engineering",
    "Hull fabrication and assembly",
    "Engine installation and systems integration",
    "Interior finishing and outfitting",
    "Safety equipment installation",
    "Maintenance and repair services"
  ];

  const capabilities = [
    {
      title: "Expert Craftsmanship",
      description: "Traditional boat-building techniques combined with modern engineering",
      icon: "🔨"
    },
    {
      title: "Quality Materials",
      description: "Premium materials ensuring durability and performance",
      icon: "⭐"
    },
    {
      title: "Safety Standards",
      description: "Compliance with international marine safety regulations",
      icon: "🛡️"
    },
    {
      title: "Custom Solutions",
      description: "Tailored designs to meet specific client requirements",
      icon: "🎨"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-cyan-600 to-cyan-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-float">
                <EngineeringIcon type="chain" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Boats & Yachts Building</h1>
            <p className="text-xl mb-8">
              Expert marine engineering and construction of boats, yachts, and watercraft. 
              Combining traditional craftsmanship with modern engineering principles.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Marine Excellence</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Custom Craftsmanship</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Safety First</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Vessel Types */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Vessel Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive marine engineering solutions for various vessel types, 
              designed for performance, safety, and reliability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {vesselTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-float-delayed">{type.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{type.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{type.description}</p>
                <ul className="space-y-2">
                  {type.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-cyan-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Capabilities */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Marine Engineering Capabilities</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Advanced marine engineering capabilities that ensure quality, safety, and performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-cyan-50 transition-colors">
                <div className="text-4xl mb-4 animate-bounce-slow">{capability.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{capability.title}</h3>
                <p className="text-gray-600 text-sm">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive marine engineering and vessel construction services from concept to completion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="wrench" size="md" className="text-cyan-500 mr-4" animated={false} />
                  <span className="font-semibold text-gray-800">{service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-cyan-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Set Sail with Excellence</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Experience the perfect blend of traditional craftsmanship and modern engineering. 
            Let's build the vessel of your dreams for the waters of Ekumfiman.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-cyan-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="chain" size="sm" animated={false} />
              Start Your Project
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-cyan-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              View Our Fleet
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarineEngineering; 